import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { isValidDeviceId, sanitizeDeviceId, sanitizePhoneNumber } from '../services/cognitoService';
import TermsAndConditions from '../components/auth/TermsAndConditions';
import SafetyGuidanceDialog from '../components/auth/SafetyGuidanceDialog';
import AuthLayout from '../components/layout/AuthLayout';
import { BRAND, fieldSx, outlineButtonSx, primaryButtonSx } from '../styles/ui';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Step labels for the registration process - Netflix style
const steps = ['Compatibility & Terms', 'Personal Details'];

const Register: React.FC = () => {
  const { register, error, isLoading, isAuthenticated } = useAuth();
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [compatibilityChecked, setCompatibilityChecked] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [safetyGuidanceChecked, setSafetyGuidanceChecked] = useState(false);
  const [openSafetyModal, setOpenSafetyModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get email from location state if available
  const emailFromState = location.state && location.state.email ? location.state.email : '';

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/profile');
    }
  }, [isAuthenticated, navigate]);

  // Form validation schema for the two-step process
  const validationSchema = [
    // Step 1: Compatibility & Terms
    Yup.object({
      termsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
      compatibilityChecked: Yup.boolean().oneOf([true], 'You must complete the Compatibility Check')
    }),
    // Step 2: Personal Details
    Yup.object({
      email: Yup.string()
        .email('Enter a valid email')
        .required('Email is required'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
          'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
        )
        .required('Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required'),
      fullName: Yup.string()
        .required('Full name is required'),
      phoneNumber: Yup.string()
        .required('Phone number is required')
        .test('is-valid-phone', 'Phone number must be valid UK format', (value) => {
          const sanitized = sanitizePhoneNumber(value);
          return sanitized.startsWith('+44') && sanitized.length >= 12;
        }),
      // Optional: users can also add it later from their account
      deviceId: Yup.string()
        .test('is-valid-device-id', "Device ID is 16 characters: digits 0-9 and letters A-F (check for the letter 'O' instead of zero)", (value) =>
          !value || isValidDeviceId(sanitizeDeviceId(value))
        ),
      safetyGuidanceChecked: Yup.boolean().oneOf([true], 'You must confirm you have read the safety guidance')
    })
  ];

  // Handle form submission
  const handleSubmit = async (values: any) => {
    setRegistrationError(null);
    try {
      await register(
        values.email,
        values.password,
        values.email, // username is same as email
        values.phoneNumber,
        values.fullName, // Use the fullName directly from the form
        values.deviceId, // Optional; left blank the account gets a placeholder until a device is linked
        (verificationWasJustSent) => {
          if (verificationWasJustSent) {
            // Only navigate if we just sent the verification code
            navigate(`/verify-account?email=${encodeURIComponent(values.email)}`);
          } else {
            // Show success message but don't navigate
            setRegistrationSuccess(true);
          }
        }
      );
    } catch (err: any) {
      setRegistrationError(err.message || 'Registration failed. Please try again.');
    }
  };

  // Handle safety guidance modal
  const handleOpenSafetyModal = () => {
    setOpenSafetyModal(true);
  };

  const handleCloseSafetyModal = () => {
    setOpenSafetyModal(false);
  };

  const handleSafetyConfirmation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSafetyGuidanceChecked(event.target.checked);
    formik.setFieldValue('safetyGuidanceChecked', event.target.checked);
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: emailFromState,
      password: '',
      confirmPassword: '',
      fullName: '',
      phoneNumber: '',
      deviceId: '',
      safetyGuidanceChecked: false,
      termsAccepted: false,
      compatibilityChecked: false
    },
    validationSchema: validationSchema[activeStep],
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (activeStep < steps.length - 1) {
        // Move to the next step if not on the last step
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        // Submit the form on the last step
        await handleSubmit(values);
      }
    }
  });
  
  // No need for useEffect since we're using the email directly in initialValues

  // Handle going back to the previous step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle terms acceptance
  const handleTermsAccept = () => {
    setTermsAccepted(true);
    formik.setFieldValue('termsAccepted', true);
    setTermsOpen(false);
  };

  // Handle view terms
  const handleViewTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    setTermsOpen(true);
  };

  const openTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    setTermsOpen(true);
  };

  // Render the current step form
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Compatibility & Terms
        return (
          <Box>
            <Typography variant="h4" component="h1" sx={{ color: BRAND.ink, mb: 2 }}>
              Terms & Conditions
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
              Accept the terms and complete the compatibility check to continue.
            </Typography>

            {isMobile && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setTermsOpen(true)}
                sx={{ ...outlineButtonSx, mb: 3 }}
              >
                Read the Terms and Conditions
              </Button>
            )}

            <FormGroup sx={{ gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      formik.setFieldValue('termsAccepted', e.target.checked);
                    }}
                    sx={{ color: BRAND.ink, '&.Mui-checked': { color: BRAND.pink } }}
                  />
                }
                label={
                  <Typography sx={{ color: BRAND.ink, fontSize: '0.9rem' }}>
                    I agree to the{' '}
                    <Link component="button" type="button" variant="body2" onClick={openTerms} sx={{ color: BRAND.pink, verticalAlign: 'baseline' }}>
                      Terms and Conditions
                    </Link>
                  </Typography>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={compatibilityChecked}
                    onChange={(e) => {
                      setCompatibilityChecked(e.target.checked);
                      formik.setFieldValue('compatibilityChecked', e.target.checked);
                    }}
                    sx={{ color: BRAND.ink, '&.Mui-checked': { color: BRAND.pink } }}
                  />
                }
                label={
                  <Typography sx={{ color: BRAND.ink, fontSize: '0.9rem' }}>
                    I have completed the{' '}
                    <Link
                      href="https://ivie.co.uk/compatibility/?srsltid=AfmBOoro3haB3nVXjpVtl2Q9JFVC70W66oWbPu6RvNDzHQZOTmxH1t9T"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: BRAND.pink }}
                    >
                      Compatibility Check
                    </Link>
                  </Typography>
                }
              />
            </FormGroup>
          </Box>
        );
      case 1: // Personal Details
        return (
          <Box>
            <Typography variant="h4" component="h1" sx={{ color: BRAND.ink, mb: 2 }}>
              Create a password to start your membership
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.muted, mb: 3 }}>
              Just a few more steps and you're done!<br />
              We hate paperwork, too.
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus={!isMobile}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email ? String(formik.errors.email) : ''}
              disabled={isLoading}
              sx={fieldSx}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formik.values.password}
              onChange={(e) => {
                formik.handleChange(e);
                // Touch the field immediately to show validation
                formik.setFieldTouched('password', true, false);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password ?
                String(formik.errors.password) :
                'Password must have at least 8 characters with uppercase, lowercase, number, and special character'}
              disabled={isLoading}
              sx={{
                ...fieldSx,
                '& .MuiFormHelperText-root': {
                  color: formik.touched.password && Boolean(formik.errors.password) ?
                    BRAND.danger : BRAND.muted,
                  fontSize: '0.75rem'
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Re-type Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formik.values.confirmPassword}
              onChange={(e) => {
                formik.handleChange(e);
                // Touch the field immediately to show validation
                formik.setFieldTouched('confirmPassword', true, false);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword ? String(formik.errors.confirmPassword) : ''}
              disabled={isLoading}
              sx={fieldSx}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="fullName"
              label="Full Name"
              name="fullName"
              autoComplete="name"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              helperText={formik.touched.fullName && formik.errors.fullName ? String(formik.errors.fullName) : ''}
              disabled={isLoading}
              sx={fieldSx}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="phoneNumber"
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              autoComplete="tel"
              placeholder="+44 or 0 prefix"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
              helperText={formik.touched.phoneNumber && formik.errors.phoneNumber ? String(formik.errors.phoneNumber) : ''}
              disabled={isLoading}
              sx={fieldSx}
            />

            <TextField
              margin="normal"
              fullWidth
              id="deviceId"
              label="Device ID (optional)"
              name="deviceId"
              autoComplete="off"
              value={formik.values.deviceId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.deviceId && Boolean(formik.errors.deviceId)}
              helperText={formik.touched.deviceId && formik.errors.deviceId ?
                String(formik.errors.deviceId) :
                'The 16-character ID on your Joan device. You can also add it later from your account.'}
              disabled={isLoading}
              inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
              sx={{
                ...fieldSx,
                '& .MuiFormHelperText-root': {
                  color: formik.touched.deviceId && Boolean(formik.errors.deviceId) ?
                    BRAND.danger : BRAND.muted,
                  fontSize: '0.75rem'
                },
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
              <FormControlLabel
                sx={{ mr: 0.5 }}
                control={
                  <Checkbox
                    checked={safetyGuidanceChecked}
                    onChange={handleSafetyConfirmation}
                    sx={{ color: BRAND.ink, '&.Mui-checked': { color: BRAND.pink } }}
                  />
                }
                label={<Typography sx={{ color: BRAND.ink, fontSize: '0.9rem' }}>I have read and understood the safety guidance</Typography>}
              />
              <IconButton
                onClick={handleOpenSafetyModal}
                aria-label="Read the safety guidance"
                sx={{ color: BRAND.ink, '&:hover': { backgroundColor: BRAND.hover } }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const isNextDisabled = () => {
    if (isLoading) return true;

    switch (activeStep) {
      case 0: // Compatibility & Terms
        return !(termsAccepted && compatibilityChecked);
      case 1: // Personal Details
        // Only check if fields are filled, not full validation
        return !formik.values.email ||
               !formik.values.password ||
               !formik.values.confirmPassword ||
               !formik.values.fullName ||
               !formik.values.phoneNumber ||
               !formik.values.safetyGuidanceChecked;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (isNextDisabled()) return;
    if (activeStep < steps.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      // Submit the form on the last step
      formik.handleSubmit();
    }
  };

  const stepsHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box component="ol" aria-label="Registration steps" sx={{ display: 'flex', gap: 4, m: 0, p: 0, listStyle: 'none' }}>
        {steps.map((step, index) => (
          <Box
            component="li"
            key={step}
            aria-current={activeStep === index ? 'step' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              opacity: activeStep === index ? 1 : 0.6,
              transition: 'opacity 0.3s ease'
            }}
          >
            <Box
              sx={{
                width: 21,
                height: 21,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: activeStep >= index ? BRAND.pink : BRAND.lineStrong,
                color: activeStep >= index ? BRAND.onPink : BRAND.ink,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                flexShrink: 0
              }}
            >
              {index + 1}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: BRAND.ink,
                fontWeight: activeStep === index ? 'bold' : 'normal',
                whiteSpace: 'nowrap',
                // Phones show only the current step's name
                display: { xs: activeStep === index ? 'block' : 'none', sm: 'block' }
              }}
            >
              {step}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <AuthLayout
        header={stepsHeader}
        footerStart={
          <Typography variant="body2" sx={{ color: BRAND.muted }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
        }
        footerActions={
          <>
            {activeStep > 0 && (
              <Button disabled={isLoading} onClick={handleBack} variant="outlined" sx={outlineButtonSx}>
                Back
              </Button>
            )}
            <Button
              type="button"
              variant="contained"
              disabled={isNextDisabled()}
              onClick={handleNext}
              sx={primaryButtonSx}
              endIcon={isLoading ? undefined : <ArrowForwardIcon />}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                'Register'
              ) : (
                'Next'
              )}
            </Button>
          </>
        }
        aside={
          <TermsAndConditions
            open={activeStep === 0 || termsOpen}
            onClose={() => setTermsOpen(false)}
            onAccept={handleTermsAccept}
            termsAccepted={termsAccepted}
          />
        }
      >
        {registrationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {registrationError}
          </Alert>
        )}

        {registrationSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Registration successful! Please check your email for the verification code.
          </Alert>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} noValidate>
          {renderStepContent()}
        </form>
      </AuthLayout>

      {isMobile && (
        <TermsAndConditions
          variant="dialog"
          open={termsOpen}
          onClose={() => setTermsOpen(false)}
          onAccept={handleTermsAccept}
          termsAccepted={termsAccepted}
        />
      )}

      <SafetyGuidanceDialog
        open={openSafetyModal}
        onClose={handleCloseSafetyModal}
        confirmed={safetyGuidanceChecked}
        onConfirmedChange={handleSafetyConfirmation}
      />
    </>
  );
};

export default Register;
